import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import useTimer from '@/hooks/use-timer.hook';
import { Button } from '@/components/ui/button/button.component';
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { clearPendingSignup, pendingSignupStore, replacePendingSignupToken } from '@/store/pending-signup.store';
import { setAuthTokens } from '@/storage/auth-tokens.storage';
import {
  resendEmailValidationCode,
  ResendEmailValidationCodeError,
  sendEmailValidationCode,
} from '@/services/auth/registration.service';
import { useToast } from '@/components/ui/use-toast';
import { resolveAuthenticatedUser } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import PinInput from '@/app/(main)/_components/sign-in-modal/_components/pin.component';
import { getLogger } from '@/logger/logger';
import { toSafeInternalPath } from '@/utils/safe-internal-path.util';

export default function PinConfirmationStep() {
  const { toast } = useToast();
  const router = useRouter();
  const [wrongPinError, setWrongPinError] = useState('');
  const { token, maskedEmail, returnUrl } = useStore(pendingSignupStore);
  const { timeLeft, startTimer, resetTimer, isActive } = useTimer({ seconds: 60 });

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  const handlePinChange = async (newPin: string) => {
    setWrongPinError('');

    if (newPin.length !== 4) {
      return;
    }

    try {
      const response = await sendEmailValidationCode(newPin, token);
      const { access_token, refresh_token } = response;
      setAuthTokens({ access_token, refresh_token });
      clearPendingSignup();
      await resolveAuthenticatedUser(access_token);
      toast.success('Conta criada com sucesso!');

      setOpenSignInModal(false);
      router.push(toSafeInternalPath(returnUrl));
    } catch (error) {
      const logger = getLogger('sign-in.pin-confirmation');
      logger.error('Failed to validate the confirmation code', {
        sign_in: { event: 'code_validation_failed' },
        error,
      });
      setWrongPinError('Código inválido');
    }
  };

  const handleResend = async () => {
    resetTimer();
    startTimer();

    try {
      const response = await resendEmailValidationCode(token);
      replacePendingSignupToken(response.token);
    } catch (error) {
      const logger = getLogger('sign-in.pin-confirmation');
      logger.error('Failed to resend the confirmation code', {
        sign_in: { event: 'code_resend_failed' },
        error,
      });
      resetTimer();
      const codeLimitExceeded = error instanceof ResendEmailValidationCodeError && error.status === 429;
      toast.error(
        codeLimitExceeded
          ? 'Limite de geração de código excedido. Tente novamente mais tarde.'
          : 'Não foi possível reenviar o código. Tente novamente.',
      );
    }
  };

  return (
    <div
      className="h-full flex flex-col outline-none outline-offset-0"
      onFocusCapture={(event) => event.preventDefault()}
      onFocus={(event) => event.preventDefault()}
      onKeyDown={(e) => e.key === 'Tab' && e.preventDefault()}
      tabIndex={-1}
    >
      <SheetHeader className="bg-white">
        <div className="flex justify-between gap-[10px] pb-6 items-center">
          <SheetTitle>
            <div className="flex gap-[10px]">
              <span
                className="font-extrabold text-2xl text-gray-900 text-nowrap"
                data-testid={DataTestIdEnum.PIN_CONFIRMATION_TITLE}
              >
                Validação de conta
              </span>
            </div>
          </SheetTitle>
          <SheetClose asChild className="self-center">
            <ModalCloseButton skipInitialFocus />
          </SheetClose>
        </div>
      </SheetHeader>

      <div className="flex flex-col">
        <div className="pb-6">
          <span className="text-gray-800 text-base">
            Insira o código de 4 dígitos que enviamos para o seu e-mail cadastrado:
            <br></br>
            <span className="font-semibold">{maskedEmail}</span>
          </span>
        </div>
        <div className="flex justify-center pb-11 flex-col">
          <PinInput onPinChange={handlePinChange} withCodePasteButton={!wrongPinError.length} />
          {wrongPinError && wrongPinError.length && <span className="text-red-500 text-sm pt-2">{wrongPinError}</span>}
        </div>

        <div className="pb-4">
          <p className="text-center text-gray-800 font-light">
            Não recebeu o código?
            <br></br>
            {isActive
              ? (
                  <>
                    Aguarde
                    {' '}
                    <span className="font-semibold whitespace-nowrap">
                      {timeLeft}
                      s
                    </span>
                    {' '}
                    para reenviar.
                  </>
                )
              : 'Clique para reenviar.'}
          </p>
        </div>
        <Button variant="secondary" color="primary" className="w-full" disabled={isActive} onClick={handleResend}>
          Reenviar
        </Button>

      </div>
    </div>
  );
}
